import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  AuditAdminListItem,
  AuditAdminListQuery,
  AuditAdminListResponse,
} from '@truecare/schemas';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminTable, type AdminTableColumn } from './AdminTable';

/**
 * Phase 14a-4 — single `<AuditTrailPanel mode={'global'|'entity-scoped'} />`
 * component used by both `/admin/audit` (mode=global; filter form is a
 * sibling) and per-entity drilldowns at 14b (mode=entity-scoped;
 * filters fixed to `{entityType, entityId}` and filter form hidden).
 *
 * Row template branches internally on mode — global rows show "Operator
 * X did Y on Caregiver Z"; entity-scoped rows skip the entity name
 * because the operator already knows the context.
 *
 * One source of truth for audit-event rendering; both modes update
 * together if the audit-event shape evolves.
 *
 * Pagination: the backend returns `totalCount`, so the panel drives the
 * shared `<AdminTable>` pagination footer. `filters.limit` sets the page
 * size; page is owned internally so any filter set (global or entity-scoped)
 * whose result exceeds one page stays reachable instead of being silently
 * truncated at 50 rows.
 */
export interface AuditTrailPanelProps {
  mode: 'global' | 'entity-scoped';
  /** Filters for the list query. Entity-scoped callers fix
   *  entityType + entityId. `page`/`limit` seed the query; page is then
   *  owned internally by the pagination footer. */
  filters: AuditAdminListQuery;
}

export function AuditTrailPanel({ mode, filters }: AuditTrailPanelProps) {
  const limit = filters.limit ?? 50;
  const [page, setPage] = useState(filters.page ?? 1);

  // Reset to page 1 whenever the filter set (anything other than page)
  // changes — e.g. the operator applies a new global filter.
  const filterKey = JSON.stringify({ ...filters, page: undefined });
  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  const effectiveFilters = { ...filters, page, limit };

  const query = useQuery<AuditAdminListResponse>({
    queryKey: ['admin', 'audit', effectiveFilters],
    queryFn: async () => {
      const result = (await unwrap(
        // The api-client surface for /admin/audit lands when api-client is
        // regenerated post-Phase-14a; until then we treat the call as
        // typed via the shared schema response type.
        api.GET('/api/v1/admin/audit', { params: { query: effectiveFilters } } as never),
      )) as unknown as AuditAdminListResponse;
      return result;
    },
  });

  const rows = query.data?.items ?? [];
  const totalCount = query.data?.totalCount ?? 0;

  // Audit list sort is not server-wired at v1, so columns are non-sortable.
  const columns: AdminTableColumn<AuditAdminListItem>[] = [
    {
      key: 'ts',
      header: 'When',
      sortable: false,
      width: 'w-48',
      render: (row) => <span className="font-mono text-xs">{row.ts}</span>,
    },
    ...(mode === 'global'
      ? [
          {
            key: 'actorUserId',
            header: 'Actor',
            sortable: false,
            render: (row: AuditAdminListItem): ReactNode => (
              <span className="font-mono text-xs">{row.actorUserId ?? 'system'}</span>
            ),
          },
        ]
      : []),
    {
      key: 'action',
      header: 'Action',
      sortable: false,
      render: (row) => row.action,
    },
    ...(mode === 'global'
      ? [
          {
            key: 'entity',
            header: 'Entity',
            sortable: false,
            render: (row: AuditAdminListItem): ReactNode => (
              <span className="font-mono text-xs">
                {row.entityType ? `${row.entityType}:${row.entityId ?? '∅'}` : '—'}
              </span>
            ),
          },
        ]
      : []),
    {
      key: 'ipAddress',
      header: 'IP',
      sortable: false,
      render: (row) => <span className="font-mono text-xs">{row.ipAddress ?? '—'}</span>,
    },
  ];

  return (
    <AdminTable
      columns={columns}
      rows={rows}
      isLoading={query.isPending}
      isError={Boolean(query.error)}
      onRetry={() => void query.refetch()}
      emptyMessage="No audit events match the current filter."
      page={page}
      limit={limit}
      totalCount={totalCount}
      onPageChange={setPage}
    />
  );
}
